/**
 * Оптимизации открытого мира «Полёта птицы» — те же, что в Экибастузе
 * (`ekibastuzWorld.ts`): инстансы и склеенные меши режутся по секторам
 * SECTOR × SECTOR м, чтобы three.js отбрасывал куски вне камеры, а сотни
 * мелких мешей одного вида в секторе сливаются в один вызов отрисовки.
 * На ТВ (webOS/Tizen) узкое место — число draw calls, а не треугольники.
 */
/* eslint-disable */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export const SECTOR = 400;
export const sectorOf = (x: number, z: number, size = SECTOR): string => `${Math.floor(x / size)}:${Math.floor(z / size)}`;

export interface InstanceItem {
  x: number; y: number; z: number;
  sx?: number; sy?: number; sz?: number;
  rx?: number; ry?: number; rz?: number;
  q?: THREE.Quaternion;
}

/** Инстансинг по секторам: один InstancedMesh на сектор, со своей bounding sphere. */
export function instancedBySector(
  scene: THREE.Scene, geo: THREE.BufferGeometry, material: THREE.Material, list: InstanceItem[], size = SECTOR,
): THREE.InstancedMesh[] {
  const meshes: THREE.InstancedMesh[] = [];
  const bySector = new Map<string, InstanceItem[]>();
  list.forEach((it) => {
    const key = sectorOf(it.x, it.z, size);
    if (!bySector.has(key)) bySector.set(key, []);
    bySector.get(key)!.push(it);
  });
  const m = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler();
  bySector.forEach((items) => {
    const mesh = new THREE.InstancedMesh(geo, material, items.length);
    items.forEach((it, i) => {
      if (it.q) q.copy(it.q); else { e.set(it.rx || 0, it.ry || 0, it.rz || 0); q.setFromEuler(e); }
      m.compose(new THREE.Vector3(it.x, it.y, it.z), q, new THREE.Vector3(it.sx || 1, it.sy || 1, it.sz || 1));
      mesh.setMatrixAt(i, m);
    });
    mesh.computeBoundingSphere();
    scene.add(mesh);
    meshes.push(mesh);
  });
  return meshes;
}

/**
 * Мелочь (деревья, фонари) рисуется только рядом с камерой: вдали это
 * пиксели в тумане, а треугольников — сотни тысяч. Куски — секторы
 * `instancedBySector`; `update` раз в кадр прячет те, чья граница дальше
 * `range` от камеры по горизонтали.
 */
export function distanceCuller(range: number): {
  add(meshes: THREE.InstancedMesh[]): void;
  update(camera: THREE.Vector3): void;
} {
  const items: { mesh: THREE.InstancedMesh; x: number; z: number; r: number }[] = [];
  return {
    add(meshes) {
      meshes.forEach((mesh) => {
        const s = mesh.boundingSphere!;
        items.push({ mesh, x: s.center.x, z: s.center.z, r: s.radius });
      });
    },
    update(camera) {
      items.forEach((it) => {
        it.mesh.visible = Math.hypot(it.x - camera.x, it.z - camera.z) - it.r < range;
      });
    },
  };
}

/**
 * Склейка статики: Lambert/Basic-меши без текстуры с одинаковым материалом в
 * одном секторе сливаются в один. Огни (`lightMaterials`) склеиваются только
 * со своим материалом — их цвет меняется со временем суток. `keep` и всё под
 * ними (анимация, звёзды, небо) не трогаются.
 */
export function mergeStaticMeshes(
  scene: THREE.Scene, keep: THREE.Object3D[], lightMaterials: THREE.Material[], size = SECTOR,
): void {
  const skip = new Set<THREE.Object3D>();
  keep.forEach((root) => root.traverse((o) => skip.add(o)));
  const lightMats = new Set(lightMaterials);
  const matKey = (mat: any): string => {
    if (lightMats.has(mat)) return mat.uuid;
    return [mat.type, mat.color?.getHex(), mat.emissive?.getHex(), mat.side, mat.flatShading,
      mat.transparent, mat.opacity, mat.vertexColors, mat.fog].join('|');
  };
  scene.updateMatrixWorld(true);
  const groups = new Map<string, THREE.Mesh[]>();
  const pos = new THREE.Vector3();
  scene.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh) || obj instanceof THREE.InstancedMesh || skip.has(obj)) return;
    // Только меши прямо на сцене: в группах — птица игры (она уже на сцене,
    // пока строится мир) и сборные модели вроде самолётов.
    if (obj.parent !== scene) return;
    const mat = obj.material as any;
    const plain = mat instanceof THREE.MeshLambertMaterial || mat instanceof THREE.MeshBasicMaterial;
    // Цвета вершин (рельеф) склейка выбросила бы — земля стала бы чёрной.
    if (Array.isArray(mat) || !plain || mat.map || mat.vertexColors) return;
    obj.getWorldPosition(pos);
    const key = `${matKey(mat)}#${sectorOf(pos.x, pos.z, size)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(obj);
  });
  groups.forEach((meshes) => {
    if (meshes.length < 2) return;
    const parts = meshes.map((mesh) => {
      const geo = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
      Object.keys(geo.attributes)
        .filter((name) => name !== 'position' && name !== 'normal')
        .forEach((name) => geo.deleteAttribute(name));
      geo.clearGroups();
      geo.applyMatrix4(mesh.matrixWorld);
      return geo;
    });
    const merged = mergeGeometries(parts, false);
    parts.forEach((g) => g.dispose());
    if (!merged) return;
    merged.computeBoundingSphere();
    meshes.forEach((mesh) => mesh.removeFromParent());
    scene.add(new THREE.Mesh(merged, meshes[0].material));
  });
}

/**
 * Материал корпуса дома с окнами в шейдере — вместо окон-кубиков (в Алматы их
 * было ~135 тыс., 1,6 млн треугольников). Сетка по мировой координате:
 * ячейка 2,6 × 3,4 м, окно 1,4 × 1,6, ночью горит половина (хэш ячейки),
 * вдали окна сливаются со стеной — иначе мелкая сетка рябит. Только для
 * корпусов-инстансов из единичного куба (`instancedBySector`): высоту дома
 * шейдер берёт из матрицы инстанса. `glow` — общий униформ 0…1, его двигает
 * время суток.
 */
export function facadeMaterial(color: number, glow: { value: number }): THREE.MeshLambertMaterial {
  const m = new THREE.MeshLambertMaterial({ color, flatShading: true });
  m.onBeforeCompile = (shader) => {
    shader.uniforms.winGlow = glow;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vFacadePos;\nvarying vec3 vFacadeN;\nvarying float vFacadeTop;')
      .replace('#include <project_vertex>', `#include <project_vertex>
        vec4 facadeWp = vec4(transformed, 1.0);
        float facadeH = 1.0;
        #ifdef USE_INSTANCING
          facadeWp = instanceMatrix * facadeWp;
          facadeH = length(instanceMatrix[1].xyz);
        #endif
        vFacadePos = (modelMatrix * facadeWp).xyz;
        vFacadeN = normal;
        vFacadeTop = (0.5 - position.y) * facadeH;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vFacadePos;\nvarying vec3 vFacadeN;\nvarying float vFacadeTop;\nuniform float winGlow;\nfloat facadeWin = 0.0;\nfloat facadeLit = 0.0;')
      .replace('#include <color_fragment>', `#include <color_fragment>
        if (abs(vFacadeN.y) < 0.5 && vFacadePos.y > 2.0 && vFacadeTop > 1.6) {
          bool alongZ = abs(vFacadeN.x) > 0.5;
          float u = alongZ ? vFacadePos.z : vFacadePos.x;
          float side = alongZ ? vFacadePos.x : vFacadePos.z;
          vec2 cell = vec2(u / 2.6, (vFacadePos.y - 1.3) / 3.4);
          vec2 f = fract(cell);
          facadeWin = step(0.23, f.x) * step(f.x, 0.77) * step(0.26, f.y) * step(f.y, 0.74);
          facadeWin *= 1.0 - smoothstep(350.0, 700.0, length(vViewPosition));
          facadeLit = step(0.5, fract(sin(dot(vec3(floor(cell), floor(side)), vec3(12.9898, 78.233, 37.719))) * 43758.5453));
          diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.353, 0.392, 0.439) * (1.0 - 0.6 * winGlow), facadeWin);
        }`)
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += facadeWin * facadeLit * winGlow * vec3(1.0, 0.835, 0.54);');
  };
  return m;
}
